import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setFaviconFromLogo, updateFavicon } from '../utils/favicon';
import { buildApiUrl } from '../config/api';
import { applyBrandColorsToElements } from '../utils/brandColors';
import { sanitizeText, sanitizeColor, sanitizeUrl } from '../utils/sanitize';

interface BrandColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

interface BrandSettings {
  brandName: string;
  organizationName: string;
  brandLogo: string;
  brandFavicon: string;
  brandColors: BrandColors;
  brandTagline: string;
  brandDescription: string;
  defaultCurrency: string;
}

interface BrandContextType {
  brand: BrandSettings;
  updateBrand: (newBrand: Partial<BrandSettings>) => Promise<void>;
  refreshBrand: () => Promise<void>;
  isLoading: boolean;
}

const defaultBrand: BrandSettings = {
  brandName: 'Ghana Family Membership System',
  organizationName: 'Nana Asaase Family System',
  brandLogo: '/logo.png',
  brandFavicon: '/favicon.ico',
  brandColors: {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    background: '#F9FAFB',
    text: '#111827'
  },
  brandTagline: 'Connecting Families, Building Futures',
  brandDescription: 'A comprehensive membership management system for Ghanaian family organizations',
  defaultCurrency: 'GHS'
};

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

interface BrandProviderProps {
  children: ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const [brand, setBrand] = useState<BrandSettings>(defaultBrand);
  const [isLoading, setIsLoading] = useState(true);

  const loadBrandSettings = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/brand'));
      if (response.ok) {
        const data = await response.json() as { success: boolean; data: BrandSettings };
        if (data.success && data.data) {
          setBrand(data.data);
          applyBrandColors(data.data.brandColors);
          applyBrandAssets(data.data);
        } else {
          setBrand(defaultBrand);
          applyBrandColors(defaultBrand.brandColors);
          applyBrandAssets(defaultBrand);
        }
      } else {
        setBrand(defaultBrand);
        applyBrandColors(defaultBrand.brandColors);
        applyBrandAssets(defaultBrand);
      }
    } catch (error) {
      console.error('Failed to load brand settings:', error);
      setBrand(defaultBrand);
      applyBrandColors(defaultBrand.brandColors);
      applyBrandAssets(defaultBrand);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBrandSettings();
  }, []);

  const applyBrandColors = (colors: BrandColors) => {
    const root = document.documentElement;
    // Sanitize colors before applying
    root.style.setProperty('--brand-primary', sanitizeColor(colors.primary));
    root.style.setProperty('--brand-secondary', sanitizeColor(colors.secondary));
    root.style.setProperty('--brand-accent', sanitizeColor(colors.accent));
    root.style.setProperty('--brand-background', sanitizeColor(colors.background));
    root.style.setProperty('--brand-text', sanitizeColor(colors.text));
    
    // Apply brand colors to UI elements
    applyBrandColorsToElements({
      primary: sanitizeColor(colors.primary),
      secondary: sanitizeColor(colors.secondary),
      accent: sanitizeColor(colors.accent),
      background: sanitizeColor(colors.background),
      text: sanitizeColor(colors.text)
    });
  };

  const applyBrandAssets = async (brandData: BrandSettings) => {
    // Update document title with brand name (sanitized)
    const newTitle = sanitizeText(brandData.brandName || 'Ghana Family Membership System');
    document.title = newTitle;
    
    // Update favicon - use logo if favicon not set
    const faviconUrl = brandData.brandFavicon || brandData.brandLogo;
    
    if (faviconUrl) {
      try {
        // If using logo as favicon, generate proper favicon
        if (!brandData.brandFavicon && brandData.brandLogo) {
          await setFaviconFromLogo(brandData.brandLogo);
        } else {
          updateFavicon(faviconUrl);
        }
      } catch (error) {
        console.warn('Failed to update favicon:', error);
        // Fallback to direct URL if available
        if (faviconUrl.startsWith('http') || faviconUrl.startsWith('data:') || faviconUrl.startsWith('/')) {
          updateFavicon(faviconUrl);
        }
      }
    }
    
    // Update meta theme-color (sanitized)
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute('content', sanitizeColor(brandData.brandColors.primary));
  };

  const updateBrand = async (newBrand: Partial<BrandSettings>) => {
    const updatedBrand = { ...brand, ...newBrand };
    setBrand(updatedBrand);
    
    // Apply changes immediately for real-time preview
    if (newBrand.brandColors) {
      applyBrandColors(updatedBrand.brandColors);
    }
    
    // Apply brand assets (favicon, title)
    await applyBrandAssets(updatedBrand);
    
    // Trigger custom event for components that need to react to brand changes
    const sanitizedBrand = {
      ...updatedBrand,
      brandName: sanitizeText(updatedBrand.brandName || ''),
      organizationName: sanitizeText(updatedBrand.organizationName || ''),
      brandTagline: sanitizeText(updatedBrand.brandTagline || ''),
      brandDescription: sanitizeText(updatedBrand.brandDescription || ''),
      brandLogo: sanitizeUrl(updatedBrand.brandLogo || ''),
      brandFavicon: sanitizeUrl(updatedBrand.brandFavicon || ''),
      defaultCurrency: sanitizeText(updatedBrand.defaultCurrency || 'GHS'),
      brandColors: {
        primary: sanitizeColor(updatedBrand.brandColors?.primary || '#3B82F6'),
        secondary: sanitizeColor(updatedBrand.brandColors?.secondary || '#10B981'),
        accent: sanitizeColor(updatedBrand.brandColors?.accent || '#F59E0B'),
        background: sanitizeColor(updatedBrand.brandColors?.background || '#F9FAFB'),
        text: sanitizeColor(updatedBrand.brandColors?.text || '#111827')
      }
    };
    window.dispatchEvent(new CustomEvent('brandUpdated', { 
      detail: { brand: sanitizedBrand } 
    }));
  };

  const refreshBrand = async () => {
    setIsLoading(true);
    await loadBrandSettings();
  };

  return (
    <BrandContext.Provider value={{ brand, updateBrand, refreshBrand, isLoading }}>
      {children}
    </BrandContext.Provider>
  );
};